import React from 'react';
import 'katex/dist/katex.min.css';
import { useMediaLoader } from '../hooks/useMediaLoader';
import { AudioMessage } from './message-types/AudioMessage';
import { MediaMessage } from './message-types/MediaMessage';
import { TextMessage } from './message-types/TextMessage';
import { ClarificationPayload } from '../types/chat';

interface MessageBubbleProps {
  isUser: boolean;
  message: {
    content: string | null | undefined;
    role: string;
    message_type: string;
    media_id?: string;
    media_path?: string;
    voice_duration?: number;
    tempAudioUrl?: string;
    tempMediaUrl?: string;
    tempFileUrl?: string;
    mime_type?: string;
    created_at?: string | Date;
    status?: 'sending' | 'sent' | 'failed';
    file_metadata?: {
      original_filename: string;
    };
    fileName?: string;
    fileSize?: number;
    fileType?: string;
    clarification?: ClarificationPayload;
    pendingHint?: 'image_processing' | 'attachment_processing';
  };
  isProfileOpen: boolean;
  onSelectClarificationOption?: (optionId: string) => void;
  onEnableClarificationOther?: () => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ 
  message, 
  isUser, 
  isProfileOpen,
  onSelectClarificationOption,
  onEnableClarificationOther,
}) => {
  const { mediaUrl } = useMediaLoader({
    messageType: message.message_type,
    mediaId: message.media_id,
    tempMediaUrl: message.tempMediaUrl,
    tempAudioUrl: message.tempAudioUrl,
    tempFileUrl: message.tempFileUrl
  });

  // Route to appropriate message type component
  if (message.message_type === 'AUDIO') {
    return (
      <AudioMessage
        message={{
          tempAudioUrl: message.tempAudioUrl,
          voice_duration: message.voice_duration,
          created_at: message.created_at,
          status: message.status
        }}
        mediaUrl={mediaUrl}
        isUser={isUser}
        isProfileOpen={isProfileOpen}
      />
    );
  }

  if (['IMAGE', 'VIDEO', 'DOCUMENT', 'FILE'].includes(message.message_type)) {
    return (
      <MediaMessage
        message={{
          message_type: message.message_type,
          content: message.content,
          fileName: message.fileName || message.file_metadata?.original_filename,
          fileSize: message.fileSize,
          status: message.status,
          created_at: message.created_at
        }}
        mediaUrl={mediaUrl}
        tempMediaUrl={message.tempMediaUrl || message.tempFileUrl}
        isUser={isUser}
        isProfileOpen={isProfileOpen}
      />
    );
  }

  // Default text message
  return (
    <TextMessage
      message={{
        content: message.content,
        media_path: message.media_path,
        status: message.status,
        created_at: message.created_at,
        clarification: message.clarification,
        pendingHint: message.pendingHint,
      }}
      isUser={isUser}
      isProfileOpen={isProfileOpen}
      onSelectClarificationOption={onSelectClarificationOption}
      onEnableClarificationOther={onEnableClarificationOther}
    />
  );
};
